import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { BillPeriod } from "../../../shared/utils/billPeriods";
import type { GasBill, GasBillDraft, GasBillsData } from "../models/GasBilling";

const moment = getMomentTz();

const toDateOnly = (value: Date | null) =>
	value === null ? null : moment.utc(value).format("YYYY-MM-DD");

const toBillDate = (value: string) => moment.utc(value).toDate();

export const getGasBills = async (
	params: { startDate: Date | null; endDate: Date | null },
	signal?: AbortSignal,
): Promise<GasBillsData> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[USG].[GetGasBills]",
		[
			{
				name: "StartDate",
				value:
					params.startDate === null
						? null
						: moment(params.startDate).format("YYYY-MM-DD"),
			},
			{
				name: "EndDate",
				value:
					params.endDate === null
						? null
						: moment(params.endDate).format("YYYY-MM-DD"),
			},
		],
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		return {
			bills: (
				get(tables, "0.rows", []) as (Omit<
					GasBill,
					"ServicePeriodStart" | "ServicePeriodEnd"
				> & { ServicePeriodStart: string; ServicePeriodEnd: string })[]
			).map((row) => ({
				GasBillId: row.GasBillId,
				ServicePeriodStart: toBillDate(row.ServicePeriodStart),
				ServicePeriodEnd: toBillDate(row.ServicePeriodEnd),
				Mcf: row.Mcf,
				Mmbtu: row.Mmbtu,
				Cost: row.Cost,
			})),
			periods: (
				get(tables, "1.rows", []) as {
					GasBillId: number;
					ServicePeriodStart: string;
					ServicePeriodEnd: string;
				}[]
			).map(
				(row): BillPeriod => ({
					billId: row.GasBillId,
					start: toBillDate(row.ServicePeriodStart),
					end: toBillDate(row.ServicePeriodEnd),
				}),
			),
		};
	} else {
		throw new Error(resp.message || "Error fetching gas bills");
	}
};

export const upsertGasBill = async (draft: GasBillDraft) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "GasBillId", value: draft.GasBillId },
		{ name: "ServicePeriodStart", value: toDateOnly(draft.ServicePeriodStart) },
		{ name: "ServicePeriodEnd", value: toDateOnly(draft.ServicePeriodEnd) },
		{ name: "Mcf", value: draft.Mcf },
		{ name: "Mmbtu", value: draft.Mmbtu },
		{ name: "Cost", value: draft.Cost },
	];

	const resp = await apiService.callV2("[USG].[UpsertGasBill]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables.0.rows.0.GasBillId", null) as number | null;
	} else {
		throw new Error(resp.message || "Error saving the gas bill");
	}
};

export const deleteGasBill = async (gasBillId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "GasBillId", value: gasBillId }];
	const resp = await apiService.callV2("[USG].[DeleteGasBill]", parameters);

	if (!resp.ok) throw new Error(resp.message || "Error deleting the gas bill");
};
